import { OUTREACH_PARAMS } from '@intent/shared';
import { ChatClient } from './core/chat-client';
import { readConfig } from './core/config';
import { EventQueue } from './core/event-queue';
import { Heartbeat } from './core/heartbeat';
import { IdentityStore } from './core/identity';
import { Tracker } from './core/tracker';
import { Transport } from './core/transport';
import { mountWidget } from './ui/mount';

/**
 * Widget entry point.
 *
 * Loaded by a single `<script>` tag on a site we do not control, so the
 * overriding rule is: never break the host page. Every failure path is
 * swallowed after being logged, and initialisation happens off the critical
 * rendering path.
 */

interface WidgetRuntime {
  visitorId: string;
  sessionId: string;
}

const GLOBAL_FLAG = '__intentChatLoaded';

async function initialise(): Promise<void> {
  const config = readConfig();

  if (!config) {
    console.warn('[intent-chat] No data-project attribute found; widget not started.');
    return;
  }

  const identity = new IdentityStore(config.projectKey);
  const transport = new Transport(config);

  const bootstrap = await transport.bootstrap({
    anonymousId: identity.getAnonymousId(),
    sessionId: identity.getSessionId(),
    url: window.location.href,
    referrer: document.referrer || null,
    title: document.title.slice(0, 300),
  });

  // The server is authoritative on identity: it may hand back a different
  // session (rotation) or confirm the anonymous id we proposed.
  identity.setAnonymousId(bootstrap.anonymousId);
  identity.setSessionId(bootstrap.sessionId);

  const runtime: WidgetRuntime = {
    visitorId: bootstrap.visitorId,
    sessionId: bootstrap.sessionId,
  };

  const queue = new EventQueue(transport, (rotatedSessionId) => {
    runtime.sessionId = rotatedSessionId;
    identity.setSessionId(rotatedSessionId);
  });

  queue.setSession(runtime);

  const tracker = new Tracker(queue);
  const heartbeat = new Heartbeat(queue, tracker);

  queue.enqueue('SESSION_START');
  tracker.start();
  heartbeat.start();

  // The landing URL (with any ic_ref/ic_ca) has now been sent to the server in
  // the bootstrap payload and captured on the first page view, so the tokens
  // have no further job in the address bar — clear them so they are never
  // bookmarked, copied, or shared. Runs in every mode, including silent.
  stripOutreachParams();

  // Track & notify only: the tracker above keeps scoring the visitor, but we
  // mount no visitor-facing UI and open no chat socket. Nothing is ever shown
  // or sent — the project just watches and gets notified.
  if (bootstrap.silent) return;

  // Chat mounts after tracking is running, so a visitor is being scored even
  // if the UI fails to render for any reason.
  const chat = new ChatClient({
    config,
    transport,
    visitorId: bootstrap.visitorId,
    sessionId: bootstrap.sessionId,
    projectName: bootstrap.project.name,
    greeting: bootstrap.project.theme.greeting,
    socketUrl: bootstrap.socketUrl,
    availabilityState: bootstrap.availabilityState,
    initialConversation: bootstrap.conversation,
  });

  chat.connect();
  mountWidget(chat, bootstrap.project.theme);
}

/**
 * Removes the outreach attribution params from the address bar, preserving
 * every other query param and the hash. Purely cosmetic and best-effort — a
 * malformed URL or a locked-down history API must never break the host page.
 */
function stripOutreachParams(): void {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const param of OUTREACH_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        changed = true;
      }
    }
    if (!changed) return;

    const query = url.searchParams.toString();
    const next = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
  } catch {
    // Swallowed on purpose: cleaning the URL is never worth a broken page.
  }
}

function start(): void {
  const globalScope = window as unknown as Record<string, boolean>;

  // A customer pasting the snippet twice must not produce double tracking.
  if (globalScope[GLOBAL_FLAG]) return;
  globalScope[GLOBAL_FLAG] = true;

  void initialise().catch((error: unknown) => {
    console.warn('[intent-chat] Failed to start:', error);
  });
}

// Defer past first paint so the host page's load is never delayed.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
